import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { Heading } from '../../components/Heading';
import { useGTM } from '../../hooks/useGTM';
import { noOp } from '../../lib/utils';
import { TrackingSchema } from '../../types';
import { RecommendationTile } from './RecommendationTile';
import { ShowMore } from './ShowMore';

export type Recommendation = {
  name: string;
  reference?: string;
  marketing_claims?: string[];
  url: string;
  image_url?: string;
  description?: string;
  manufacturer?: string;
  itemNumber?: string;
  [key: string]: unknown;
};

type RecommendationsProps = {
  initialLimit: number;
  recommendations: Recommendation[];
  recommendationType: 'formulation' | 'ingredient';
  placeholderImage: string;
  heading: string;
  reportRecommendationsExist?: (exists: boolean) => void;
};

export const RecommendationsSection: FC<RecommendationsProps> = ({
  initialLimit,
  recommendations,
  recommendationType,
  placeholderImage,
  heading,
  reportRecommendationsExist = noOp,
}) => {
  const [amountToShow, setAmountToShow] = useState(initialLimit);
  const SHOW_MORE_INCREMENT = 6;
  const { updateDataLayer } = useGTM();

  useEffect(() => {
    reportRecommendationsExist(!!recommendations.length);
  }, [recommendations, reportRecommendationsExist]);

  const onRecClick = useCallback(
    (rec: Recommendation) => {
      const recIsFormulation = recommendationType === 'formulation';
      const data: { [key: string]: string | string[] } = {};

      if (recIsFormulation) {
        data.type = 'formulation';
        data.reference = rec.reference || 'No reference available';
      } else {
        data.type = 'product';
        data.name = rec.name;
        data.id = rec.itemNumber || 'No id available';
        data.manufacturer = rec.manufacturer || 'No manufacturer available';
      }

      data.marketingClaims =
        rec.marketing_claims || 'No marketing claims available';

      updateDataLayer({
        event: `Teaser ${recIsFormulation ? 'Formulation' : 'Product'} Click`,
        schema: TrackingSchema.teaser,
        data,
      });
      return true;
    },
    [recommendationType, updateDataLayer],
  );

  return (
    <>
      {recommendations.length > 0 && (
        <div data-testid={`${recommendationType}Recommendations`}>
          <div className="flex flex-col flex-wrap flex-grow xl:max-w-6xl mx-3 font-regular">
            <div className="flex justify-center">
              <Heading
                level="h3"
                align="center"
                weight="medium"
                margin={7}
                text={heading}
              />
            </div>
            <div className="max-w-full">
              <div className="grid sm:grid-cols-3 space-x-3">
                {recommendations
                  .slice(0, amountToShow)
                  .map((dataItem, index) => (
                    <Fragment
                      key={index}
                    >
                      <RecommendationTile
                        recommendation={dataItem}
                        placeholderImage={placeholderImage}
                        onClick={(recommendation) => onRecClick(recommendation)}
                      />
                    </Fragment>
                  ))}
              </div>
            </div>
            {recommendations.length > amountToShow && (
              <ShowMore
                increaseAmount={() =>
                  setAmountToShow(amountToShow + SHOW_MORE_INCREMENT)
                }
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
